import * as React from "react"
import { Check, X, Loader2 } from "lucide-react"
import { cn } from "@/lib/utils"

export type ValidationState = 'success' | 'error' | 'checking' | 'unavailable';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: boolean;
  labelColor?: string;
  showFloatingLabel?: boolean;
  style?: React.CSSProperties & { '--input-gap'?: string };
  validationState?: ValidationState;
  validationMessage?: string;
  /**
   * Optional element rendered absolutely on the right of the input — used for
   * trailing controls like a password show/hide toggle. Mutually exclusive
   * with `validationState`: when both are set, the validation icon wins so
   * we never lose the validity signal.
   */
  trailingSlot?: React.ReactNode;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, placeholder, value, error, labelColor = '#fff', showFloatingLabel = true, style, validationState, validationMessage, trailingSlot, onBlur, onFocus, ...props }, ref) => {
    const [isFocused, setIsFocused] = React.useState(false);
    const hasValue = value && value.toString().length > 0;

    const hasValidationIcon = !!validationState;
    const showTrailing = !hasValidationIcon && !!trailingSlot;
    const hasTrailingPadding = hasValidationIcon || showTrailing;
    const borderClass = validationState === 'success'
      ? 'border-sossiety-success'
      : (validationState === 'error' || validationState === 'unavailable' || error)
        ? 'border-red-500'
        : 'border-[rgba(255,255,255,0.1)]';

    return (
      <div className="flex flex-col">
        {showFloatingLabel && ((type === 'date' && placeholder === 'Birthday') || isFocused || hasValue) && (
          <label className="text-xs" style={{ color: labelColor }}>
            {placeholder}
          </label>
        )}
        <div className="relative">
          <input
            type={type}
            placeholder={!showFloatingLabel || (!isFocused && !hasValue) ? placeholder : ""}
            value={value}
            onFocus={(e) => { setIsFocused(true); onFocus?.(e); }}
            onBlur={(e) => { setIsFocused(false); onBlur?.(e); }}
            className={cn(
              "flex h-12 w-full box-border rounded-xl px-3 py-2 text-base focus:outline-none disabled:cursor-not-allowed disabled:opacity-50",
              "border bg-sossiety-input text-white placeholder:text-white/40",
              borderClass,
              hasTrailingPadding && "pr-12",
              className,
              error && !validationState && "border-red-500",
              type === 'date' && "[&::-webkit-calendar-picker-indicator]:invert"
            )}
            ref={ref}
            style={style}
            {...props}
          />
          {hasValidationIcon && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
              {validationState === 'success' && <Check className="w-4 h-4 text-sossiety-success" />}
              {(validationState === 'error' || validationState === 'unavailable') && <X className="w-4 h-4 text-red-500" />}
              {validationState === 'checking' && <Loader2 className="w-4 h-4 text-white/50 animate-spin" />}
            </span>
          )}
          {showTrailing && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center">
              {trailingSlot}
            </span>
          )}
        </div>
        {validationMessage && (
          <p className={cn(
            "mt-1 flex items-center gap-1 text-xs font-medium",
            validationState === 'success' ? 'text-sossiety-success' : 'text-red-300'
          )}>
            {validationMessage}
          </p>
        )}
      </div>
    )
  }
)
Input.displayName = "Input"

export { Input }
